import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Box, Sparkles, Layers, GraduationCap, CircleDollarSign, Loader2, Send, Plus,
  CheckCircle2, AlertTriangle, Trophy, Package, Eye, ShieldCheck, Lock, Mic, MicOff,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PortalNav from "@/components/PortalNav";
import EmailVerifyCard from "@/components/EmailVerifyCard";
import SalesAcademy from "@/components/sales/SalesAcademy";
import GlbPreview, { GlbDownload, AssetImage } from "@/components/creator/GlbPreview";
import LocalKitTab, { GlbUpload } from "@/components/creator/LocalKitTab";
import CreatorSupportChat from "@/components/creator/CreatorSupportChat";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-[11px] text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";
const CATEGORIES = ["Rigged Clothing", "Modular Kit", "Props", "Environment", "Weapons", "Vehicles", "Characters"];
const STATUS_META = {
  draft: ["DRAFT", "text-zinc-400 border-white/20"],
  in_review: ["IN REVIEW", "text-nc-orange border-nc-orange/40 bg-nc-orange/10"],
  published: ["PUBLISHED", "text-nc-green border-nc-green/40 bg-nc-green/10"],
  rejected: ["REJECTED", "text-red-400 border-red-500/40 bg-red-500/10"],
};

function ForgeTab({ me, onJobsChanged }) {
  const [prompt, setPrompt] = useState("");
  const [assets, setAssets] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [genBusy, setGenBusy] = useState(false);
  const [busy3d, setBusy3d] = useState("");
  const [viewer, setViewer] = useState("");
  const [voiceOn, setVoiceOn] = useState(false);
  const voiceRec = useRef(null);
  const jobsRef = useRef([]);
  const viewerRef = useRef("");
  const VOICE_OK = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const cycleViewer = (dir) => {
    const done = jobsRef.current.filter((j) => j.status === "done");
    if (!done.length) {
      toast.error("No completed models to view yet");
      return;
    }
    const idx = done.findIndex((j) => j.id === viewerRef.current);
    const next = done[(idx + dir + done.length) % done.length];
    setViewer(next.id);
    toast.success(`Viewing model ${done.indexOf(next) + 1} of ${done.length}`);
  };

  // Voice canvas commands: dictate the prompt, then say "generate" — fully hands-free creation
  const toggleVoice = () => {
    if (!VOICE_OK) {
      toast.error("Voice prompting needs Chrome or Edge — the browser provides the microphone engine");
      return;
    }
    if (voiceOn) {
      try { voiceRec.current?.stop(); } catch { /* noop */ }
      voiceRec.current = null;
      setVoiceOn(false);
      return;
    }
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new Rec();
    rec.lang = "en-AU";
    rec.continuous = true;
    rec.interimResults = false;
    rec.onresult = (e) => {
      const raw = e.results[e.results.length - 1][0].transcript.trim();
      const lower = raw.toLowerCase().replace(/[.!?]$/, "");
      // 3D viewer commands (relayed to the open model preview)
      if (["rotate", "spin", "stop rotating", "stop spinning", "pause rotation"].includes(lower)) { window.dispatchEvent(new CustomEvent("forge-viewer-cmd", { detail: "rotate" })); return; }
      if (lower === "zoom in") { window.dispatchEvent(new CustomEvent("forge-viewer-cmd", { detail: "zoom-in" })); return; }
      if (lower === "zoom out") { window.dispatchEvent(new CustomEvent("forge-viewer-cmd", { detail: "zoom-out" })); return; }
      if (["next model", "next"].includes(lower)) { cycleViewer(1); return; }
      if (["previous model", "previous model please", "previous"].includes(lower)) { cycleViewer(-1); return; }
      if (["close viewer", "close preview", "close model"].includes(lower)) { setViewer(""); return; }
      if (/^(generate|generate it|create it|build it|go)$/.test(lower)) {
        document.querySelector('[data-testid="forge-generate-button"]')?.click();
        return;
      }
      if (lower === "clear prompt") { setPrompt(""); return; }
      if (lower === "stop dictation") {
        try { rec.stop(); } catch { /* noop */ }
        voiceRec.current = null;
        setVoiceOn(false);
        return;
      }
      setPrompt((p) => (p ? `${p} ` : "") + raw.replace(/[.!?]$/, ""));
    };
    rec.onend = () => { if (voiceRec.current) { try { rec.start(); } catch { /* already running */ } } };
    rec.onerror = () => {};
    voiceRec.current = rec;
    try {
      rec.start();
      setVoiceOn(true);
      toast.success('Listening — speak your prompt, then say "generate"');
    } catch { /* permission denied */ }
  };

  useEffect(() => { // keep voice commands pointed at fresh state
    jobsRef.current = jobs;
    viewerRef.current = viewer;
  }, [jobs, viewer]);

  useEffect(() => () => { // stop the mic when leaving the tab
    try { voiceRec.current?.stop(); } catch { /* noop */ }
    voiceRec.current = null;
  }, []);

  const load = useCallback(() => {
    api.get("/creators/forge/assets").then((r) => setAssets(r.data)).catch(() => {});
    api.get("/creators/forge/jobs").then((r) => setJobs(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while any job is queued/processing
  useEffect(() => {
    if (!jobs.some((j) => j.status === "queued" || j.status === "processing")) return undefined;
    const t = setInterval(() => api.get("/creators/forge/jobs").then((r) => setJobs(r.data)).catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [jobs]);

  const generate = async () => {
    setGenBusy(true);
    try {
      const r = await api.post("/creators/forge/concept", { prompt });
      toast.success("Concept generated — review it below");
      setPrompt("");
      load();
      return r.data;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGenBusy(false);
    }
  };

  const sendTo3d = async (assetId) => {
    setBusy3d(assetId);
    try {
      await api.post("/creators/forge/3d", { asset_id: assetId });
      toast.success("Sent to TRELLIS — GLB lands in 1-3 minutes");
      api.get("/creators/forge/jobs").then((r) => setJobs(r.data)).catch(() => {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy3d("");
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[400px_1fr] gap-5 items-start">
      {/* Concept generator */}
      <div className="border border-white/10 bg-black/40">
        <div className="border-b border-white/10 px-5 py-3.5 font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Step 1 // Concept Engine
        </div>
        <div className="p-5 flex flex-col gap-3">
          <div>
            <div className="flex items-center justify-between gap-2">
              <label className={labelCls}>Asset prompt</label>
              <button type="button" data-testid="forge-voice-mic" aria-label={voiceOn ? "Stop voice dictation" : "Dictate your prompt by voice"} aria-pressed={voiceOn} onClick={toggleVoice} disabled={!VOICE_OK && !voiceOn}
                className={`font-mono text-[9px] tracking-[0.2em] uppercase px-2.5 py-1.5 border flex items-center gap-1.5 transition-colors ${voiceOn ? "border-nc-orange text-nc-orange bg-nc-orange/10" : VOICE_OK ? "border-white/15 text-zinc-400 hover:border-nc-blue hover:text-nc-blue" : "border-white/5 text-zinc-700 cursor-not-allowed"}`}>
                {voiceOn ? <MicOff className="w-3 h-3 animate-pulse" /> : <Mic className="w-3 h-3" />} {voiceOn ? "Listening…" : "Voice"}
              </button>
            </div>
            <textarea data-testid="forge-prompt-input" rows={5} className={`${inputCls} resize-y leading-relaxed`} placeholder={"Modular sci-fi corridor wall panel, brushed gunmetal with orange accents, game-ready low-poly style, single object on plain dark background, orthographic view"} value={prompt} onChange={(e) => setPrompt(e.target.value)} maxLength={1500} />
            {voiceOn && <p data-testid="forge-voice-hint" role="status" className="font-mono text-[9px] text-nc-orange mt-1.5 leading-relaxed">Listening — speak your prompt. Say "generate" to create · "rotate" / "zoom in" / "next model" while viewing a model · "clear prompt" to start over · "stop dictation" to finish.</p>}
          </div>
          <p className="font-mono text-[9px] text-zinc-600 leading-relaxed">One object per concept, plain background, orthographic view — the 3D converter rewards clarity. Budget: 20 concepts/day.</p>
          <button data-testid="forge-generate-button" disabled={genBusy || prompt.trim().length < 10} onClick={generate} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-3 flex items-center justify-center gap-2 transition-colors">
            {genBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {genBusy ? "Generating (~10s)…" : "Generate Concept"}
          </button>
          <div className="border-t border-white/10 pt-3 mt-1">
            <p className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase mb-2">Your concepts ({assets.length})</p>
            {assets.length === 0 && <p className="font-mono text-[10px] text-zinc-600">No concepts yet — generate your first above.</p>}
            <div className="grid grid-cols-2 gap-2 max-h-[420px] overflow-y-auto pr-1">
              {assets.map((a) => (
                <div key={a.id} data-testid={`forge-asset-${a.id}`} className="border border-white/10 bg-[#050505]/60 group">
                  <AssetImage assetId={a.id} alt={a.prompt} className="w-full aspect-square object-cover" testid={`forge-asset-img-${a.id}`} />
                  <div className="p-2">
                    <p className="font-mono text-[8px] text-zinc-500 line-clamp-2 leading-relaxed">{a.prompt}</p>
                    <button data-testid={`forge-send3d-${a.id}`} disabled={busy3d === a.id} onClick={() => sendTo3d(a.id)} className="mt-1.5 w-full font-mono text-[9px] tracking-[0.15em] uppercase px-2 py-1.5 bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black transition-colors flex items-center justify-center gap-1.5">
                      {busy3d === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Box className="w-3 h-3" />} Send to 3D
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 3D jobs */}
      <div className="border border-white/10 bg-black/40">
        <div className="border-b border-white/10 px-5 py-3.5 font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center justify-between gap-2">
          <span className="flex items-center gap-2"><Box className="w-3.5 h-3.5" /> Step 2 // 3D Models ({jobs.length})</span>
          <GlbUpload onUploaded={load} />
        </div>
        <div className="p-5 flex flex-col gap-3">
          {jobs.length === 0 && <p data-testid="forge-jobs-empty" className="font-mono text-[10px] text-zinc-600">No 3D models yet — send a concept from the left. Budget: 10 generations/day.</p>}
          {jobs.map((j) => (
            <div key={j.id} data-testid={`forge-job-${j.id}`} className="border border-white/10 bg-[#050505]/60 p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <AssetImage assetId={j.asset_id} alt="" className="w-12 h-12 object-cover border border-white/10" />
                  <div>
                    <p className="font-mono text-[10px] text-zinc-200">forge-{j.id.slice(0, 8)}.glb</p>
                    <p className="font-mono text-[8px] text-zinc-600">{String(j.created_at).slice(0, 16).replace("T", " ")}{j.glb_size ? ` // ${(j.glb_size / 1048576).toFixed(1)} MB` : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`font-mono text-[8px] tracking-[0.2em] uppercase border px-2 py-0.5 ${j.status === "done" ? "text-nc-green border-nc-green/40" : j.status === "failed" ? "text-red-400 border-red-500/40" : "text-nc-orange border-nc-orange/40"}`}>{j.status}</span>
                  {j.status === "done" && (
                    <>
                      <button data-testid={`forge-view-${j.id}`} onClick={() => setViewer(viewer === j.id ? "" : j.id)} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-nc-blue/50 text-nc-blue hover:bg-nc-blue/10 transition-colors flex items-center gap-1.5">
                        <Eye className="w-3 h-3" /> {viewer === j.id ? "Hide" : "View"}
                      </button>
                      <GlbDownload jobId={j.id} />
                    </>
                  )}
                </div>
              </div>
              {j.status === "failed" && <p className="font-mono text-[9px] text-red-400 mt-2 flex items-start gap-1.5"><AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" /> {j.error}</p>}
              {(j.status === "queued" || j.status === "processing") && (
                <p className="font-mono text-[9px] text-nc-orange mt-2 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> TRELLIS is building your mesh — this takes 1-3 minutes…</p>
              )}
              {viewer === j.id && j.status === "done" && <div className="mt-3"><GlbPreview jobId={j.id} /></div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PacksTab({ me, trained }) {
  const [packs, setPacks] = useState([]);
  const [assets, setAssets] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: CATEGORIES[0], cover_asset_id: "", job_ids: [] });
  const [busy, setBusy] = useState(false);
  const [precheck, setPrecheck] = useState(null); // { packId, data }

  const load = useCallback(() => {
    api.get("/creators/packs").then((r) => setPacks(r.data)).catch(() => {});
    api.get("/creators/forge/assets").then((r) => setAssets(r.data)).catch(() => {});
    api.get("/creators/forge/jobs").then((r) => setJobs(r.data.filter((j) => j.status === "done"))).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleJob = (id) => setForm((f) => ({ ...f, job_ids: f.job_ids.includes(id) ? f.job_ids.filter((x) => x !== id) : [...f.job_ids, id] }));

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/creators/packs", form);
      toast.success("Pack draft created");
      setCreating(false);
      setForm({ title: "", description: "", category: CATEGORIES[0], cover_asset_id: "", job_ids: [] });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (id) => {
    setBusy(true);
    try {
      const r = await api.post(`/creators/packs/${id}/submit`);
      toast.success(r.data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const runPrecheck = async (id) => {
    setBusy(true);
    try {
      const r = await api.get(`/creators/packs/${id}/precheck`);
      setPrecheck({ packId: id, data: r.data });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/creators/packs/${id}`);
      toast.success("Pack deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="font-mono text-[10px] text-zinc-500">Assemble models into sellable packs. Submissions {trained ? "unlock after the academy" : "are gated by the academy"} — studio QA reviews within 48h.</p>
        <button data-testid="pack-new-button" onClick={() => setCreating((c) => !c)} className="font-mono text-[9px] tracking-[0.2em] uppercase px-3 py-2 border border-nc-blue/40 text-nc-blue hover:bg-nc-blue/10 transition-colors flex items-center gap-1.5">
          <Plus className="w-3 h-3" /> New Pack
        </button>
      </div>

      {creating && (
        <div data-testid="pack-create-form" className="border border-nc-blue/30 bg-nc-blue/5 p-5 flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Pack title *</label>
              <input data-testid="pack-title-input" className={inputCls} placeholder="Neo-Tokyo Streetwear Pack Vol.1" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} maxLength={120} />
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select data-testid="pack-category-select" className={inputCls} value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Description * (min 20 chars)</label>
            <textarea data-testid="pack-description-input" rows={3} className={`${inputCls} resize-y`} placeholder="What's inside, engine targets, poly budget notes…" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} maxLength={4000} />
          </div>
          <div>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
              <label className={labelCls}>Cover concept (storefront thumbnail)</label>
              <button
                type="button"
                data-testid="cover-studio-button"
                disabled={busy || !form.cover_asset_id || form.title.trim().length < 4}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const r = await api.post("/creators/packs/cover-studio", { asset_id: form.cover_asset_id, title: form.title.trim(), category: form.category });
                    toast.success("Pro cover made — selected as your pack cover");
                    setForm((f) => ({ ...f, cover_asset_id: r.data.id }));
                    load();
                  } catch (err) {
                    toast.error(formatApiErrorDetail(err.response?.data?.detail));
                  } finally {
                    setBusy(false);
                  }
                }}
                className="font-mono text-[8px] tracking-[0.15em] uppercase border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 disabled:opacity-40 px-2.5 py-1.5 transition-colors"
              >
                Cover Studio // add pro title overlay
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {assets.map((a) => (
                <button key={a.id} type="button" data-testid={`pack-cover-${a.id}`} onClick={() => setForm((f) => ({ ...f, cover_asset_id: a.id }))} className={`border-2 transition-colors ${form.cover_asset_id === a.id ? "border-nc-blue" : "border-transparent"}`}>
                  <AssetImage assetId={a.id} alt="" className="w-16 h-16 object-cover" />
                </button>
              ))}
              {assets.length === 0 && <p className="font-mono text-[9px] text-zinc-600">Generate concepts in the Forge tab first.</p>}
            </div>
          </div>
          <div>
            <label className={labelCls}>Models in this pack ({form.job_ids.length} selected)</label>
            <div className="flex flex-wrap gap-2">
              {jobs.map((j) => (
                <button key={j.id} type="button" data-testid={`pack-job-${j.id}`} onClick={() => toggleJob(j.id)} className={`font-mono text-[9px] px-2.5 py-1.5 border transition-colors flex items-center gap-1.5 ${form.job_ids.includes(j.id) ? "text-nc-green border-nc-green/50 bg-nc-green/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>
                  <Box className="w-3 h-3" /> forge-{j.id.slice(0, 8)}
                </button>
              ))}
              {jobs.length === 0 && <p className="font-mono text-[9px] text-zinc-600">No completed 3D models yet.</p>}
            </div>
          </div>
          <div className="flex gap-2">
            <button data-testid="pack-create-submit" disabled={busy || form.title.trim().length < 4 || form.description.trim().length < 20} onClick={create} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2.5 transition-colors">
              {busy ? "Saving…" : "Create Draft"}
            </button>
            <button onClick={() => setCreating(false)} className="font-mono text-[9px] uppercase text-zinc-400 px-3">Cancel</button>
          </div>
        </div>
      )}

      {packs.length === 0 && !creating && <p data-testid="packs-empty" className="font-mono text-[10px] text-zinc-600 border border-white/10 p-5">No packs yet — create your first draft above.</p>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {packs.map((p, i) => {
          const [label, cls] = STATUS_META[p.status] || STATUS_META.draft;
          return (
            <div key={p.id} data-testid={`pack-${i + 1}`} className="border border-white/10 bg-black/40">
              <div className="flex">
                {p.cover_asset_id && <AssetImage assetId={p.cover_asset_id} alt={p.title} className="w-24 h-24 object-cover border-r border-white/10 shrink-0" testid={`pack-cover-img-${i + 1}`} />}
                <div className="p-4 min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-sans font-bold text-sm text-zinc-100 truncate">{p.title}</p>
                    <span data-testid={`pack-status-${i + 1}`} className={`font-mono text-[8px] tracking-[0.2em] uppercase border px-1.5 py-0.5 shrink-0 ${cls}`}>{label}</span>
                  </div>
                  <p className="font-mono text-[9px] text-zinc-500 mt-1">{p.category} // {(p.job_ids || []).length} model(s)</p>
                  {p.status === "published" && (
                    <p className="font-mono text-[9px] text-nc-green mt-1">AUD {p.price_aud} // bounty AUD {p.bounty_aud} // {p.royalty_pct}% royalty</p>
                  )}
                </div>
              </div>
              {p.status === "rejected" && p.review_notes && (
                <p className="font-mono text-[9px] text-red-400 border-t border-white/5 px-4 py-2.5 flex items-start gap-1.5"><AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" /> {p.review_notes}</p>
              )}
              {p.status === "draft" && (
                <div className="border-t border-white/5 px-4 py-2.5 flex flex-col gap-2.5">
                  <div className="flex gap-2 flex-wrap">
                    <button data-testid={`pack-submit-${i + 1}`} disabled={busy} onClick={() => submit(p.id)} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-1.5 bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black transition-colors flex items-center gap-1.5">
                      <Send className="w-3 h-3" /> Submit for Review
                    </button>
                    <button data-testid={`pack-precheck-${i + 1}`} disabled={busy} onClick={() => runPrecheck(p.id)} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-1.5 border border-nc-blue/50 text-nc-blue hover:bg-nc-blue/10 disabled:opacity-40 transition-colors flex items-center gap-1.5">
                      <ShieldCheck className="w-3 h-3" /> QA Pre-Check
                    </button>
                    <button data-testid={`pack-delete-${i + 1}`} disabled={busy} onClick={() => remove(p.id)} className="font-mono text-[9px] uppercase px-3 py-1.5 text-red-400 border border-red-500/40 hover:bg-red-500/10 transition-colors">Delete</button>
                  </div>
                  {precheck?.packId === p.id && (
                    <div data-testid={`pack-precheck-panel-${i + 1}`} className="border border-white/10 bg-[#050505] p-3.5 flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <p className={`font-mono text-[10px] uppercase tracking-[0.15em] ${precheck.data.ready ? "text-nc-green" : "text-nc-orange"}`}>
                          {precheck.data.ready ? "Ready for QA" : "Not ready"} — score {precheck.data.score}/100
                        </p>
                        <button onClick={() => setPrecheck(null)} className="font-mono text-[8px] uppercase text-zinc-600 hover:text-zinc-200">close</button>
                      </div>
                      {precheck.data.checks.map((c) => (
                        <div key={c.id} className="flex items-start gap-2">
                          {c.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-nc-green shrink-0 mt-0.5" /> : <AlertTriangle className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${c.required ? "text-red-400" : "text-nc-orange"}`} />}
                          <div>
                            <p className={`font-mono text-[10px] ${c.ok ? "text-zinc-400" : "text-zinc-200"}`}>{c.label}{!c.required && <span className="text-zinc-600"> (recommended)</span>}</p>
                            {!c.ok && <p className="font-mono text-[9px] text-zinc-500 mt-0.5 leading-relaxed">{c.hint}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReferralCard() {
  const [refs, setRefs] = useState(null);
  useEffect(() => {
    api.get("/creators/referrals").then((r) => setRefs(r.data)).catch(() => {});
  }, []);
  if (!refs?.recruitment_id) return null;
  const link = `${window.location.origin}/join-creators?rec=${refs.recruitment_id}`;
  return (
    <div data-testid="creator-referral-card" className="border border-nc-orange/40 bg-nc-orange/5 p-5 flex flex-col gap-3">
      <p className="font-sans font-bold text-xs text-zinc-100 uppercase flex items-center gap-2"><Trophy className="w-4 h-4 text-nc-orange" /> Recruit creators — earn AUD {refs.bonus_aud} each</p>
      <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">
        Share your link. When a creator who signed up through it <b className="text-zinc-200">activates</b>, you get
        <b className="text-nc-orange"> AUD {refs.bonus_aud}</b> straight into your claimable balance. No cap.
      </p>
      <div className="flex items-center gap-2 bg-[#050505] border border-white/10 px-3 py-2">
        <code data-testid="referral-link" className="font-mono text-[10px] text-nc-orange truncate flex-1">{link}</code>
        <button data-testid="referral-copy" onClick={async () => { const { safeCopy } = await import("@/lib/api"); (await safeCopy(link)) ? toast.success("Referral link copied") : toast.error("Copy blocked"); }} className="font-mono text-[8px] uppercase text-zinc-500 hover:text-white shrink-0 transition-colors">copy</button>
      </div>
      <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.15em]">
        {refs.signups} signed up · {refs.activated} activated · <span className="text-nc-green">AUD {refs.earned.toFixed(2)} earned</span>
      </p>
    </div>
  );
}

function EarningsTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/creators/earnings").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const payout = async () => {
    setBusy(true);
    try {
      const r = await api.post("/creators/earnings/payout");
      toast.success(r.data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <p className="font-mono text-[10px] text-zinc-600 animate-pulse">Loading earnings…</p>;

  return (
    <div className="flex flex-col gap-4">
      <ReferralCard />
      <div className="grid grid-cols-3 gap-3">
        <div className="border border-nc-green/30 bg-nc-green/5 px-4 py-5 text-center">
          <p data-testid="creator-claimable" className="font-sans font-bold text-xl text-nc-green">AUD {data.claimable.toFixed(2)}</p>
          <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em] mt-1">Claimable</p>
        </div>
        <div className="border border-white/10 px-4 py-5 text-center">
          <p className="font-sans font-bold text-xl text-zinc-100">AUD {data.payout_pending.toFixed(2)}</p>
          <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em] mt-1">Payout Pending</p>
        </div>
        <div className="border border-white/10 px-4 py-5 text-center">
          <p className="font-sans font-bold text-xl text-zinc-100">AUD {data.paid.toFixed(2)}</p>
          <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em] mt-1">Paid Out</p>
        </div>
      </div>
      {data.dsp && (
        <div data-testid="earnings-dsp-card" className="border border-nc-orange/40 bg-nc-orange/5 p-5 flex flex-col gap-3">
          <p className="font-sans font-bold text-xs text-zinc-100 uppercase flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-nc-orange" /> DSP-Safe Ledger — pension protection active</p>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em]">Monthly cap</p>
              <p data-testid="dsp-cap" className="font-sans font-bold text-base text-zinc-100 mt-0.5">AUD {data.dsp.cap_aud.toFixed(2)}</p>
            </div>
            <div>
              <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em]">Paid out this month</p>
              <p data-testid="dsp-used" className="font-sans font-bold text-base text-zinc-100 mt-0.5">AUD {data.dsp.used_this_month_aud.toFixed(2)}</p>
            </div>
            <div>
              <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em]">Room left this month</p>
              <p data-testid="dsp-remaining" className="font-sans font-bold text-base text-nc-green mt-0.5">AUD {data.dsp.remaining_this_month_aud.toFixed(2)}</p>
            </div>
          </div>
          {data.dsp.held_aud > 0 && (
            <p data-testid="dsp-held-note" className="font-mono text-[10px] text-nc-orange leading-relaxed border-t border-nc-orange/20 pt-3">
              AUD {data.dsp.held_aud.toFixed(2)} is safely held in your ledger — it unlocks on {data.dsp.unlock_date}, keeping every fortnight inside your cap. Your plan manager receives these statements automatically.
            </p>
          )}
          {data.dsp.rollovers.length > 0 && (
            <div className="border-t border-nc-orange/20 pt-2.5 flex flex-col gap-1.5">
              <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em]">Recent rollovers</p>
              {data.dsp.rollovers.slice(0, 4).map((r, i) => (
                <p key={r.id} data-testid={`dsp-rollover-${i + 1}`} className="font-mono text-[9px] text-zinc-400">
                  {String(r.created_at).slice(0, 10)} — requested AUD {r.requested_aud.toFixed(2)}, released {r.released_aud.toFixed(2)}, held {r.rolled_over_aud.toFixed(2)}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
      {data.claimable > 0 && (
        <button data-testid="creator-payout-button" disabled={busy} onClick={payout} className="self-start bg-nc-green hover:bg-nc-green/80 disabled:opacity-40 text-black font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-5 py-3 flex items-center gap-2 transition-colors">
          <CircleDollarSign className="w-4 h-4" /> {busy ? "Requesting…" : `Claim AUD ${data.claimable.toFixed(2)}`}
        </button>
      )}
      <div className="border border-white/10">
        <div className="border-b border-white/10 px-4 py-2.5 bg-white/5 font-mono text-[9px] tracking-[0.25em] text-zinc-400 uppercase">Earnings Ledger</div>
        {data.entries.length === 0 && <p data-testid="earnings-empty" className="font-mono text-[10px] text-zinc-600 p-4">No earnings yet — get a pack approved for your first bounty.</p>}
        {data.entries.map((e, i) => (
          <div key={e.id} data-testid={`earning-${i + 1}`} className="flex items-center justify-between border-b border-white/5 px-4 py-3">
            <div className="flex items-center gap-3">
              {e.type === "bounty" ? <Trophy className="w-4 h-4 text-nc-orange" /> : <Package className="w-4 h-4 text-nc-blue" />}
              <div>
                <p className="font-mono text-[10px] text-zinc-200 uppercase">{e.type} {e.type === "royalty" ? "// pack sale" : "// pack approved"}</p>
                <p className="font-mono text-[8px] text-zinc-600">{String(e.created_at).slice(0, 16).replace("T", " ")}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-mono text-xs text-zinc-100">AUD {e.amount.toFixed(2)}</p>
              <p className={`font-mono text-[8px] uppercase ${e.status === "claimable" ? "text-nc-green" : e.status === "paid" ? "text-zinc-500" : "text-nc-orange"}`}>{e.status.replace("_", " ")}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CreatorPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [tab, setTab] = useState("forge");
  const [busy, setBusy] = useState(false);
  const [activation, setActivation] = useState(null);
  const [payBusy, setPayBusy] = useState(false);
  const [previewAcademy, setPreviewAcademy] = useState(false);
  const [a11y, setA11y] = useState(null);

  const load = useCallback(() => {
    api.get("/auth/me").then((r) => {
      setMe(r.data);
      if (r.data.role !== "creator" && r.data.role !== "admin") {
        navigate("/join-creators");
      }
    }).catch(() => navigate("/auth"));
  }, [navigate]);

  const loadOnboarding = useCallback(() => {
    api.get("/creators/onboarding").then((r) => {
      setOnboarding(r.data);
      if (!r.data.done) setTab("academy");
    }).catch(() => {});
    api.get("/creators/activate/status").then((r) => setActivation(r.data)).catch(() => {});
    api.get("/ndis/my-flags").then((r) => setA11y(r.data)).catch(() => {});
  }, []);

  const startPayment = async () => {
    setPayBusy(true);
    try {
      const r = await api.post("/creators/activate/checkout", { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPayBusy(false);
    }
  };

  useEffect(() => { load(); loadOnboarding(); }, [load, loadOnboarding]);

  const completeStep = async (stepId) => {
    setBusy(true);
    try {
      const r = await api.post(`/creators/onboarding/${stepId}/complete`);
      setOnboarding(r.data);
      if (r.data.done) {
        toast.success("Academy complete — pack submissions unlocked");
        setTab("forge");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!me) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><p className="font-mono text-xs text-zinc-600 animate-pulse">Loading the Forge…</p></div>;

  const needsVerify = me.role === "creator" && !me.email_verified_at && String(me.created_at || "") >= "2026-08-10T00:00:00+00:00";
  if (needsVerify) {
    return (
      <div data-testid="creator-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col">
        <PortalNav title="Creator Forge" />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-lg">
            <EmailVerifyCard email={me.email} testid="creator-email-verify" onVerified={() => window.location.reload()} />
          </div>
        </div>
      </div>
    );
  }

  if (me.role === "creator" && activation && !activation.active) {
    if (previewAcademy) {
      return (
        <div data-testid="creator-academy-preview" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col">
          <PortalNav title="Creator Forge" />
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full flex flex-col gap-5">
            <button data-testid="preview-back-button" onClick={() => setPreviewAcademy(false)} className="self-start font-mono text-[9px] tracking-[0.25em] uppercase text-nc-blue hover:text-zinc-100 transition-colors">
              &larr; Back to activation
            </button>
            {onboarding && <SalesAcademy onboarding={onboarding} onComplete={completeStep} busy={busy} title="Creator Academy — Preview" subtitle="Complete steps now; submissions unlock after activation" testid="creator-academy" certificatePath="/creators/onboarding/certificate/pdf" certificateName="specpixel-creator-academy-certificate.pdf" />}
          </div>
        </div>
      );
    }
    return (
      <div data-testid="creator-paywall" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30 relative overflow-hidden">
        <PortalNav title="Creator Forge" />
        {/* backdrop: security texture + glows */}
        <div className="absolute inset-0 grid-texture opacity-30 animate-grid-drift pointer-events-none" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-nc-blue/10 blur-[140px] rounded-full pointer-events-none animate-pulse-slow" />
        <div className="flex-1 flex items-center justify-center p-4 relative">
          <motion.div className="w-full max-w-2xl flex flex-col gap-5" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
            <div className="text-center">
              <p className="font-mono text-[10px] tracking-[0.4em] text-nc-blue uppercase mb-2 flex items-center justify-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5 text-nc-green" /> Secure Activation // Encrypted Checkout
              </p>
              <h1 className="font-sans font-bold uppercase tracking-tight text-2xl sm:text-4xl text-zinc-50">Activate your Creator Forge</h1>
              <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.2em] mt-2">Application approved — final step</p>

              {/* onboarding progress stepper — where am I in the process? */}
              <div data-testid="onboarding-stepper" className="flex items-center justify-center gap-0 mt-6 flex-wrap">
                {[
                  { n: 1, label: "Apply", state: "done" },
                  { n: 2, label: "Verify email", state: me.email_verified_at ? "done" : "now" },
                  { n: 3, label: "Activate — US$" + activation.fee_usd, state: me.email_verified_at ? "now" : "next" },
                  { n: 4, label: "Academy", state: "next" },
                  { n: 5, label: "Submit packs, get paid", state: "next" },
                ].map((s, i, arr) => (
                  <div key={s.n} className="flex items-center">
                    <div data-testid={`stepper-step-${s.n}`} className="flex flex-col items-center gap-1.5 px-2">
                      <span className={`w-7 h-7 rounded-full border flex items-center justify-center font-mono text-[10px] ${
                        s.state === "done" ? "border-nc-green bg-nc-green/15 text-nc-green" : s.state === "now" ? "border-nc-orange bg-nc-orange/15 text-nc-orange animate-pulse" : "border-white/15 text-zinc-600"
                      }`}>{s.state === "done" ? "✓" : s.n}</span>
                      <span className={`font-mono text-[7px] tracking-[0.1em] uppercase whitespace-nowrap ${s.state === "now" ? "text-nc-orange" : s.state === "done" ? "text-zinc-400" : "text-zinc-600"}`}>{s.label}</span>
                    </div>
                    {i < arr.length - 1 && <div className={`w-6 h-px ${s.state === "done" ? "bg-nc-green/50" : "bg-white/10"}`} />}
                  </div>
                ))}
              </div>
              {!me.email_verified_at && (
                <div data-testid="stepper-verify-warning" className="mt-4 border border-nc-orange/40 bg-nc-orange/5 px-4 py-3 max-w-md mx-auto">
                  <p className="font-mono text-[10px] text-zinc-300 leading-relaxed">
                    <b className="text-nc-orange">You're on step 2:</b> check your inbox for the verification email first — payment unlocks right after. Then come back here and hit the pay button below.
                  </p>
                </div>
              )}
              {me.email_verified_at && (
                <div className="mt-4 border border-nc-blue/40 bg-nc-blue/5 px-4 py-3 max-w-md mx-auto">
                  <p className="font-mono text-[10px] text-zinc-300 leading-relaxed">
                    <b className="text-nc-blue">You're on step 3 of 5 — this is the only payment in the whole program.</b>{" "}
                    One-time US${activation.fee_usd} buys the Local Forge Kit forever. Nothing is subscription, nothing recurs, and your first approved pack pays it back with change.
                  </p>
                </div>
              )}
            </div>

            {/* tracing-beam card */}
            <div className="relative overflow-hidden p-[1.5px]">
              <motion.div
                className="absolute w-[200%] h-[200%] left-[-50%] top-[-50%]"
                style={{ background: "conic-gradient(from 0deg, transparent 0deg, transparent 300deg, rgba(46,107,255,0.9) 340deg, rgba(0,230,118,0.9) 355deg, transparent 360deg)" }}
                animate={{ rotate: 360 }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
              />
              <div className="relative bg-[#0a0a0a] p-6 sm:p-8 flex flex-col gap-5 scanline-texture">
                <div className="flex items-center justify-between">
                  <div className="flex items-baseline gap-2">
                    <span data-testid="paywall-fee" className="font-sans font-bold text-5xl text-zinc-50">US${activation.fee_usd}</span>
                    <span className="font-mono text-[10px] text-zinc-500 uppercase">one-time — no subscriptions, ever</span>
                  </div>
                  <div className="flex items-center gap-1.5 border border-nc-green/40 bg-nc-green/5 px-2.5 py-1.5">
                    <Lock className="w-3 h-3 text-nc-green" />
                    <span className="font-mono text-[8px] tracking-[0.2em] text-nc-green uppercase">Secure</span>
                  </div>
                </div>
                <p className="font-mono text-[11px] text-zinc-300 leading-relaxed max-w-lg">
                  Your activation buys the <b className="text-nc-blue">Local Forge Kit</b> — the complete setup to run
                  TRELLIS 3D generation on <b>your own computer</b>. No studio credit limits, no queues: you generate
                  locally for life, upload GLBs directly, and earn bounty + royalties on every pack.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    ["Local TRELLIS.2 Kit", "The studio's image-to-3D engine running on your own GPU — unlimited generations"],
                    ["Rig & Export Standards", "Blender rig-prep checklist, naming conventions, GLB export specs that pass QA first time"],
                    ["Lifetime Pipeline Access", "Direct GLB upload, pack builder, QA review, marketplace storefront + royalty tracking"],
                  ].map(([t, d], i) => (
                    <motion.div key={i} data-testid={`paywall-perk-${i + 1}`} className="border border-white/10 bg-black/40 p-4 hover:border-nc-blue/30 transition-colors" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.1, duration: 0.4 }}>
                      <p className="font-sans font-bold text-xs text-zinc-100 uppercase">{t}</p>
                      <p className="font-mono text-[9px] text-zinc-500 mt-1.5 leading-relaxed">{d}</p>
                    </motion.div>
                  ))}
                </div>
                <div className="border border-nc-green/30 bg-nc-green/5 px-4 py-3 flex items-center gap-3">
                  <CheckCircle2 className="w-4 h-4 text-nc-green shrink-0" />
                  <p className="font-mono text-[10px] text-zinc-300 leading-relaxed">
                    <b className="text-nc-green">Earn-back guarantee:</b> your first approved pack pays a bounty of at least AUD 30 — this activation comes straight back with change.
                  </p>
                </div>
                <motion.button
                  data-testid="paywall-pay-button"
                  disabled={payBusy}
                  onClick={startPayment}
                  className="bg-nc-blue disabled:opacity-40 text-white font-sans font-bold uppercase tracking-[0.2em] text-xs px-6 py-4 flex items-center justify-center gap-2 animate-cta-breathe"
                  whileHover={{ scale: 1.02, boxShadow: "0 0 25px rgba(46,107,255,0.5)" }}
                  whileTap={{ scale: 0.98 }}
                >
                  {payBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                  {payBusy ? "Opening secure checkout…" : `Pay US$${activation.fee_usd} — Activate via Stripe`}
                </motion.button>
                <div className="flex items-center justify-center gap-4 flex-wrap">
                  {["256-bit encrypted", "Stripe secured", "Cards + crypto", "14-day refund policy"].map((t) => (
                    <span key={t} className="font-mono text-[8px] tracking-[0.15em] text-zinc-600 uppercase flex items-center gap-1">
                      <CheckCircle2 className="w-2.5 h-2.5 text-zinc-600" /> {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <button data-testid="paywall-academy-link" onClick={() => setPreviewAcademy(true)} className="font-mono text-[9px] tracking-[0.25em] uppercase text-zinc-500 hover:text-zinc-200 transition-colors self-center">
              Preview the Creator Academy first
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  const CREATOR_TABS = [["forge", "AI Forge", Sparkles], ["packs", "Packs", Layers], ["kit", "Local Kit", Package], ["academy", "Academy", GraduationCap], ["earnings", "Earnings", CircleDollarSign]];

  // Adaptive dashboard: NDIS "Neuro-Affirming Lite Interface" profile → high-visibility 3-button layout
  if (a11y?.registered && (a11y.accessibility_profiles || []).includes("lite_interface")) {
    const LITE = [["forge", "Create a 3D Asset", Sparkles], ["packs", "My Packs", Layers], ["earnings", "My Earnings", CircleDollarSign]];
    const needsAcademy = onboarding && !onboarding.done;
    return (
      <div data-testid="creator-page-lite" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col">
        <PortalNav title="Creator Forge" />
        <div className="flex-1 max-w-2xl mx-auto px-6 py-10 w-full flex flex-col gap-6">
          <div>
            <h1 className="font-sans font-bold text-3xl text-zinc-50">Hi {String(me.name || "Creator").split(" ")[0]}</h1>
            <p className="font-mono text-xs text-zinc-400 mt-2">Pick one big button. That's all you need to do.</p>
          </div>
          {needsAcademy ? (
            <SalesAcademy onboarding={onboarding} onComplete={completeStep} busy={busy} title="Creator Academy" subtitle="Complete each step at your own pace — there's no deadline" testid="creator-academy" certificatePath="/creators/onboarding/certificate/pdf" certificateName="specpixel-creator-academy-certificate.pdf" />
          ) : (
            <>
              <div className="flex flex-col gap-4" role="navigation" aria-label="Creator tools">
                {LITE.map(([t, label, Icon]) => (
                  <button key={t} data-testid={`creator-lite-${t}`} onClick={() => setTab(t)} className={`flex items-center gap-4 border p-6 text-left transition-colors min-h-[72px] ${tab === t ? "border-nc-orange bg-nc-orange/10" : "border-white/15 hover:border-white/40"}`}>
                    <Icon className={`w-7 h-7 ${tab === t ? "text-nc-orange" : "text-nc-blue"}`} />
                    <span className="font-sans font-semibold text-xl">{label}</span>
                  </button>
                ))}
              </div>
              <div data-testid="creator-lite-content" className="border border-white/10 bg-black/40 p-4">
                {tab === "forge" && <ForgeTab me={me} />}
                {tab === "packs" && <PacksTab me={me} trained={false} />}
                {tab === "earnings" && <EarningsTab />}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  const voiceMode = a11y?.registered && (a11y.accessibility_profiles || []).includes("voice_control");

  return (
    <div data-testid="creator-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <PortalNav title="Creator Forge" />
      {voiceMode && (
        <div data-testid="creator-voice-banner" className="border-b border-nc-blue/30 bg-nc-blue/5 px-6 py-3">
          <p className="font-mono text-[10px] text-nc-blue tracking-[0.15em]">Hands-free mode ready — open Accessibility Settings (top right) and enable Voice Control, then say "click …", "focus …", or "type …" to work hands-free.</p>
        </div>
      )}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-[240px_1fr]">
        {/* sidebar — ops console (desktop) */}
        <aside className="hidden md:flex flex-col border-r border-white/10 bg-[#070707]">
          <div className="px-5 py-6 border-b border-white/10">
            <p className="font-mono text-[9px] tracking-[0.35em] text-nc-blue uppercase mb-1.5">Specpixel // AI Creator Program</p>
            <p className="font-sans font-bold uppercase text-lg text-zinc-50 flex items-center gap-2">
              <Box className="w-5 h-5 text-nc-blue" /> Creator Forge
            </p>
          </div>
          <nav data-testid="creator-tabs" className="flex flex-col py-3">
            {CREATOR_TABS.map(([t, label, Icon]) => (
              <button
                key={t}
                data-testid={`creator-tab-${t}`}
                onClick={() => setTab(t)}
                className={`flex items-center gap-3 px-5 py-3.5 font-mono text-[10px] tracking-[0.25em] uppercase border-l-2 transition-colors ${
                  tab === t
                    ? "border-nc-orange bg-zinc-900 text-zinc-50"
                    : "border-transparent text-zinc-500 hover:text-zinc-200 hover:bg-zinc-900/50"
                }`}
              >
                <Icon className={`w-4 h-4 ${tab === t ? "text-nc-orange" : ""}`} strokeWidth={1.75} /> {label}
              </button>
            ))}
          </nav>
          <div className="mt-auto px-5 py-5 border-t border-white/10 flex flex-col gap-3">
            <div>
              <p className="font-mono text-[8px] tracking-[0.25em] text-zinc-600 uppercase mb-1.5">Academy progress</p>
              <div className="h-1 bg-white/10">
                <div className="h-full bg-nc-green transition-all duration-500" style={{ width: `${onboarding ? ((onboarding.completed || []).length / (onboarding.steps?.length || 8)) * 100 : 0}%` }} />
              </div>
              <p className="font-mono text-[9px] mt-1.5 uppercase">
                <span className={onboarding?.done ? "text-nc-green" : "text-nc-orange"}>{onboarding?.done ? "Complete" : `${(onboarding?.completed || []).length}/${onboarding?.steps?.length || 8} steps`}</span>
              </p>
            </div>
            {me.founding_creator && (
              <div className="border border-nc-orange/40 bg-nc-orange/5 px-3 py-2 flex items-center gap-2">
                <Trophy className="w-3.5 h-3.5 text-nc-orange" />
                <span className="font-mono text-[8px] tracking-[0.2em] text-nc-orange uppercase">Founding Creator</span>
              </div>
            )}
          </div>
        </aside>

        {/* mobile tabs */}
        <div className="md:hidden border-b border-white/10 px-4 py-3 flex gap-2 overflow-x-auto">
          {CREATOR_TABS.map(([t, label, Icon]) => (
            <button key={t} data-testid={`creator-tab-mobile-${t}`} onClick={() => setTab(t)} className={`font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors flex items-center gap-2 whitespace-nowrap ${tab === t ? "border-nc-orange/60 text-nc-orange bg-nc-orange/10" : "border-white/10 text-zinc-400"}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {/* content */}
        <div className="relative">
          <div className="absolute inset-0 grid-texture opacity-20 pointer-events-none" />
          <motion.div
            key={tab}
            className="relative max-w-6xl mx-auto px-4 sm:px-6 py-8 w-full flex flex-col gap-6"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          >
            {tab === "academy" && onboarding && (
              <SalesAcademy onboarding={onboarding} onComplete={completeStep} busy={busy} title="Creator Academy — Mandatory Training" subtitle="Complete all 8 steps to unlock pack submissions" testid="creator-academy" certificatePath="/creators/onboarding/certificate/pdf" certificateName="specpixel-creator-academy-certificate.pdf" />
            )}
            {tab === "forge" && <ForgeTab me={me} />}
            {tab === "kit" && <LocalKitTab />}
            {tab === "packs" && <PacksTab me={me} trained={!onboarding?.done} />}
            {tab === "earnings" && <EarningsTab />}
          </motion.div>
        </div>
      </div>
      <CreatorSupportChat />
    </div>
  );
}
